import React from "react";
import { Camera, UploadCloud } from "lucide-react";
import AppSelect from "../../../../components/AppSelect";

export function PrimaryButton({ onClick, disabled, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-xl bg-[#61013B] px-5 py-3 text-base font-semibold text-white transition hover:bg-[#4d0130] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {label}
    </button>
  );
}

export function ChipButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border px-3 py-2.5 text-base font-semibold transition ${
        active
          ? "border-[#61013B] bg-[#61013B] text-white"
          : "border-[#CEB0C2] bg-white text-[#344054]"
      }`}
    >
      {children}
    </button>
  );
}

export function SelectionGroup({ label, count, options, values, onToggle }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-base font-semibold text-[#344054]">{label}</label>
        <span className="text-sm font-medium text-[#667085]">{count} Selected</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <ChipButton
            key={option}
            active={values.includes(option)}
            onClick={() => onToggle(option)}
          >
            {option}
          </ChipButton>
        ))}
      </div>
    </div>
  );
}

export function UploadBox({
  title,
  helper,
  fileName,
  buttonLabel,
  onClick,
  preview,
  previewAlt,
  children,
  className = "",
}) {
  return (
    <div
      className={`rounded-2xl border border-dashed border-[#D0D5DD] bg-white p-5 text-center ${className}`}
    >
      {preview ? (
        <div className="mx-auto mb-4 h-24 w-24 overflow-hidden rounded-full border border-[#E4E7EC]">
          <img src={preview} alt={previewAlt || title} className="h-full w-full object-cover" />
        </div>
      ) : (
        <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-[#D0D5DD] bg-white text-[#667085]">
          {title.toLowerCase().includes("profile") ? (
            <Camera className="h-5 w-5" />
          ) : (
            <UploadCloud className="h-5 w-5" />
          )}
        </div>
      )}
      <p className="text-base font-semibold text-[#1D2939]">{title}</p>
      <p className="mt-1 text-sm text-[#667085]">{helper}</p>
      <p className="mt-3 text-sm font-medium text-[#475467]">
        {fileName || "No file uploaded yet."}
      </p>
      <button
        type="button"
        onClick={onClick}
        className="mt-4 inline-flex items-center justify-center rounded-lg bg-[#E7CCD9] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#d8b2c4]"
      >
        {buttonLabel}
      </button>
      {children}
    </div>
  );
}

export function FieldShell({ label, children }) {
  return (
    <div className="space-y-2">
      <label className="text-base font-semibold text-[#344054]">{label}</label>
      {children}
    </div>
  );
}

export function TextInputField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  inputMode,
  disabled = false,
}) {
  return (
    <FieldShell label={label}>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        disabled={disabled}
        className="w-full rounded-xl border border-[#D0D5DD] bg-white px-4 py-3 text-base font-medium text-[#344054] placeholder:text-[#98A2B3] focus:border-[#61013B] focus:outline-none focus:ring-2 focus:ring-[#61013B]/10 disabled:bg-[#F9FAFB]"
      />
    </FieldShell>
  );
}

export function TextAreaField({ label, value, onChange, rows, placeholder }) {
  return (
    <FieldShell label={label}>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full rounded-xl border border-[#D0D5DD] bg-white px-4 py-3 text-base font-medium text-[#344054] placeholder:text-[#98A2B3] focus:border-[#61013B] focus:outline-none focus:ring-2 focus:ring-[#61013B]/10"
      />
    </FieldShell>
  );
}

export function InlinePricingRow({ label, value, onChange, placeholder }) {
  return (
    <div className="grid items-center gap-3 md:grid-cols-[minmax(0,1fr)_260px]">
      <label className="text-base font-medium text-[#1D2939]">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        inputMode="decimal"
        className="w-full rounded-xl border border-[#D0D5DD] bg-white px-4 py-3 text-base font-medium text-[#344054] placeholder:text-[#98A2B3] focus:border-[#61013B] focus:outline-none focus:ring-2 focus:ring-[#61013B]/10"
      />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, placeholder }) {
  return (
    <FieldShell label={label}>
      <AppSelect
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
      />
    </FieldShell>
  );
}
